import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { AdminBusinessDetail } from '../../../types/admin';
import { formatDistanceToNow } from 'date-fns';

interface BusinessAgentsTabProps {
  business: AdminBusinessDetail;
}

/**
 * BusinessAgentsTab - Display all agents with performance metrics
 *
 * Features:
 * - Agent list with avatars
 * - Performance metrics (clients, conversion rate, revenue)
 * - Active/inactive status
 * - Top performers highlight
 * - Sortable by revenue
 */
export default function BusinessAgentsTab({ business }: BusinessAgentsTabProps) {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const isTopPerformer = (agentId: string): boolean => {
    const topPerformerEmails = business.analytics.agents.top_performers.map((p) =>
      business.agents.find((a) => a.name === p.name)?.email
    );
    const agent = business.agents.find((a) => a.id === agentId);
    return topPerformerEmails.includes(agent?.email);
  };

  const getPerformanceColor = (conversionRate: number): string => {
    if (conversionRate >= 0.4) return 'success';
    if (conversionRate >= 0.25) return 'primary';
    return 'warning';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Summary Stats */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Agent Performance Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Agents
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {business.analytics.agents.total}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Active Agents
            </Typography>
            <Typography variant="h5" fontWeight={600} color="success.main">
              {business.analytics.agents.active}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Avg Clients per Agent
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {business.analytics.agents.avg_clients_per_agent}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Top Performers */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.100' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <StarIcon color="warning" />
          <Typography variant="h6" fontWeight={600}>
            Top Performers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {business.analytics.agents.top_performers.map((performer, index) => (
            <Box key={index} sx={{ minWidth: 200 }}>
              <Typography variant="body2" fontWeight={500}>
                {performer.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {formatCurrency(performer.revenue)} revenue
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {performer.clients} active clients
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Agents Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Agent</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Total Clients</TableCell>
              <TableCell align="right">Active Clients</TableCell>
              <TableCell align="right">Conversion Rate</TableCell>
              <TableCell align="right">Total Revenue</TableCell>
              <TableCell align="right">Monthly Revenue</TableCell>
              <TableCell>Last Activity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {business.agents.map((agent) => (
              <TableRow
                key={agent.id}
                hover
                sx={{
                  bgcolor: isTopPerformer(agent.id) ? 'warning.50' : 'inherit',
                }}
              >
                {/* Agent Info */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar src={agent.picture} alt={agent.name} sx={{ width: 40, height: 40 }} />
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {agent.name}
                        </Typography>
                        {isTopPerformer(agent.id) && (
                          <Tooltip title="Top Performer">
                            <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="a"
                        href={`mailto:${agent.email}`}
                        sx={{
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {agent.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Chip
                    label={agent.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={agent.is_active ? 'success' : 'default'}
                  />
                </TableCell>

                {/* Total Clients */}
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    <PeopleIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={500}>
                      {agent.clients_count}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Active Clients */}
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500} color="success.main">
                    {agent.active_clients_count}
                  </Typography>
                </TableCell>

                {/* Conversion Rate */}
                <TableCell align="right">
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {(agent.conversion_rate * 100).toFixed(0)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={agent.conversion_rate * 100}
                      color={getPerformanceColor(agent.conversion_rate) as 'success' | 'primary' | 'warning'}
                      sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                    />
                  </Box>
                </TableCell>

                {/* Total Revenue */}
                <TableCell align="right">
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="caption" display="block">
                          Total: {formatCurrency(agent.total_revenue)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Avg per client: {formatCurrency(agent.total_revenue / agent.active_clients_count || 0)}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <AttachMoneyIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(agent.total_revenue).replace('$', '')}
                      </Typography>
                    </Box>
                  </Tooltip>
                </TableCell>

                {/* Monthly Revenue */}
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(agent.monthly_revenue).replace('$', '')}/mo
                  </Typography>
                </TableCell>

                {/* Last Activity */}
                <TableCell>
                  <Tooltip title={new Date(agent.last_activity).toLocaleString()}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDistanceToNow(new Date(agent.last_activity), { addSuffix: true })}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {business.agents.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No agents found for this business
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
